import { useCallback, useState, type SubmitEvent } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { acceptInvitation, type AccountWait } from "../../api/accountsApi";
import { previewInvitation, type InvitationPreview } from "../../api/invitationsApi";
import ErrorNotice from "../../errors/ErrorNotice";
import { systemGroupName } from "../access";
import { itemKindText } from "../workflows/inputCards/typeWords";
import { useLoad } from "../../shared/loading/useLoad";
import { useSubmission } from "../../shared/state/useSubmission";
import AppButton from "../../ui/buttons/AppButton";
import LoadStatus from "../../ui/feedback/LoadStatus";
import StatusMessage from "../../ui/feedback/StatusMessage";
import { FORM_CONTROL_CLASS } from "../../ui/styles/formStyles";
import AuthCardPage from "./AuthCardPage";
import BackToSignInLink from "./BackToSignInLink";
import NewPasswordFields from "./NewPasswordFields";
import Field from "../../ui/forms/Field";

/** The token may come in the fragment, `/invitation#token=…`, which browsers never send to a server. */
const TOKEN_FRAGMENT_PARAMETER = "token";

function tokenFromFragment(hash: string): string {
  return new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash).get(TOKEN_FRAGMENT_PARAMETER) ?? "";
}

/**
 * `/invitation`: accept an invitation. With the link's token it first says who invited the
 * person and what they get (F133); a token typed in is checked on accepting. Accepting creates
 * the account and puts it into the invitation's groups at once, with no approval (GS-7); the page
 * then says what the account still waits for, as the server says. The inviter passes the link on.
 */
export default function InvitationAcceptancePage() {
  const location = useLocation();
  const linkToken = tokenFromFragment(location.hash);
  // Another invitation's link opened in the same tab starts afresh: its offer, its acceptance.
  return <InvitationAcceptance key={linkToken} linkToken={linkToken} />;
}

function InvitationAcceptance({ linkToken }: { linkToken: string }) {
  const { t } = useTranslation();
  const loadLink = useCallback(
    (signal: AbortSignal) => (linkToken === "" ? Promise.resolve(null) : previewInvitation(linkToken, signal)),
    [linkToken],
  );
  const { state: link, reload } = useLoad(loadLink);
  const [accepted, setAccepted] = useState<{ readonly waitsFor: AccountWait | null } | null>(null);

  return (
    <AuthCardPage title={t("invitation.title")}>
      {accepted !== null ? <AcceptedMessage waitsFor={accepted.waitsFor} /> : (
        <>
          <LoadStatus state={link} loadingLabel={t("common.loading")} onRetry={reload} />
          {link.status === "loaded" ? (
            link.value !== null && link.value.state !== "pending" ? (
              <StatusMessage tone="info" className="mb-4">{t(`invitation.notPending.${link.value.state}`)}</StatusMessage>
            ) : (
              <>
                {link.value?.preview ? <OfferSummary offer={link.value.preview} /> : null}
                <AcceptanceForm linkToken={linkToken} onAccepted={(waitsFor) => { setAccepted({ waitsFor }); }} />
              </>
            )
          ) : null}
        </>
      )}
      <BackToSignInLink />
    </AuthCardPage>
  );
}

function OfferSummary({ offer }: { offer: InvitationPreview }) {
  const { t } = useTranslation();
  const { holding } = offer;
  // What they get, as the server works it out, a line each: the groups they join, an
  // administrator's the installation's Administrators first (GS-7), then what those let them do
  // there and what they curate.
  const joins = offer.accountRole === "administrator" ? [systemGroupName(t, "administrators"), ...offer.joins] : offer.joins;
  const gets = [
    joins.length === 0 ? null : t("invitation.join", { groups: joins.join(", ") }),
    holding === null || holding.permissions.length === 0 ? null : t("invitation.may", { permissions: holding.permissions.map((permission) => t(`access.permissions.${permission}`)).join(", ") }),
    holding === null || holding.curatedKinds.length === 0 ? null : t("invitation.curates", { kinds: holding.curatedKinds.map((kind) => itemKindText(kind, t)).join(", ") }),
  ].filter((part) => part !== null);
  return (
    <div className="mb-4 space-y-1 text-sm">
      <p>
        {offer.projectName === null
          ? t("invitation.offer", { name: offer.invitedBy })
          : t("invitation.offerProject", { name: offer.invitedBy, project: offer.projectName })}
      </p>
      {gets.map((line) => <p key={line}>{line}</p>)}
      <p className="app-text-muted">{t("invitation.forEmail", { email: offer.email })}</p>
    </div>
  );
}

function AcceptedMessage({ waitsFor }: { waitsFor: AccountWait | null }) {
  const { t } = useTranslation();
  return (
    <StatusMessage tone="success" className="mb-4">
      {waitsFor === null ? t("invitation.ready") : t(`waitingApproval.waitsFor.${waitsFor}`)}
    </StatusMessage>
  );
}

function AcceptanceForm({ linkToken, onAccepted }: { linkToken: string; onAccepted: (waitsFor: AccountWait | null) => void }) {
  const { t } = useTranslation();
  const { state, submit } = useSubmission();
  const [typedToken, setTypedToken] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [mismatch, setMismatch] = useState(false);

  const handleSubmit = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (password !== confirmation) {
      setMismatch(true);
      return;
    }
    setMismatch(false);
    submit(async () => {
      const waitsFor = await acceptInvitation({ acceptanceToken: (linkToken === "" ? typedToken : linkToken).trim(), displayName, password });
      setPassword("");
      setConfirmation("");
      onAccepted(waitsFor);
    });
  };

  return (
    <form onSubmit={handleSubmit}>
      <fieldset disabled={state.status === "submitting"}>
        {linkToken === "" ? (
          <Field id="invitation-token" label={t("invitation.token")}>
            {(control) => (
              <input {...control} type="password" className={`mb-3 ${FORM_CONTROL_CLASS}`} value={typedToken}
                onChange={(event) => { setTypedToken(event.target.value); }} autoComplete="off" required />
            )}
          </Field>
        ) : null}
        <Field id="invitation-name" label={t("newAccount.displayName")}>
          {(control) => (
            <input {...control} className={`mb-3 ${FORM_CONTROL_CLASS}`} value={displayName}
              onChange={(event) => { setDisplayName(event.target.value); }} autoComplete="name" required />
          )}
        </Field>
        <NewPasswordFields
          idPrefix="invitation"
          password={password}
          confirmation={confirmation}
          onPasswordChange={setPassword}
          onConfirmationChange={setConfirmation}
        />
        {mismatch ? <StatusMessage tone="error" className="mb-3">{t("newAccount.passwordMismatch")}</StatusMessage> : null}
        {state.status === "failed" ? <ErrorNotice error={state.error} className="mb-3" /> : null}
        <AppButton type="submit" className="w-full" variant="primary">
          {state.status === "submitting" ? t("login.pleaseWait") : t("invitation.submit")}
        </AppButton>
      </fieldset>
    </form>
  );
}
